import type { CatalogFacet, CatalogStation } from '../../domain/catalog';
import { findCatalogMatch } from '../../domain/library';
import { plural, type TranslationKey, translate } from '../../i18n';
import { clear, el, icon } from '../../lib/dom';
import { rewireCombobox, rewireDs } from '../../lib/ds';
import type { AppState } from '../../store/app';
import { fillPicker, isCoarsePointer, presentSheet } from '../components/action-sheet';
import { createSearchField } from '../components/search-field';
import { formatStationDescription, formatStationTech } from '../format';

/**
 * Zaslon Iskanje — spletna baza postaj (P2 §2).
 *
 * Vrstica je namerno **ožja** od tiste na zaslonu Postaje: ni ★, ni kebaba,
 * ni potega. Zadetek še ni uporabnikov in dejanja, ki jih na njem ni mogoče
 * izvesti, ne smejo biti vidna. Ostaneta dve: predposlušaj in dodaj.
 *
 *   .ao-list
 *     .ao-row
 *       button.ao-row__main    PREDPOSLUŠAJ
 *       .ao-row__acts
 *         button.ao-row__act   DODAJ — ali značka »na seznamu«
 *
 * ✅ **Filtra sta od DS v4.20.10 naprej `.cmb`** (D2 §29). Do takrat sta bila
 * `.select-wrap select`, ker je DS imel za combobox sloge, ne pa modula JS —
 * lastna izvedba bi pomenila komponento, definirano na strani namesto v
 * `components.css`, kar zlata pravila prepovedujejo.
 *
 * Kar smo s tem dobili, je **iskanje po izbirah**: pri 241 državah je razlika
 * med spustnim seznamom in iskalnim izbirnikom razlika med uporabnim in
 * neuporabnim. Vzorec ARIA, tipkovnico in `aria-activedescendant` nosi modul.
 */

/**
 * Štiri prazna stanja, ne eno (P4 §6).
 *
 * »Še nisem iskal«, »ni zadetkov«, »vir ni odgovoril« in »ni povezave« so
 * štirje različni položaji z **različnimi potmi ven**. Eno samo sporočilo bi
 * uporabniku ob prvem odprtju zaslona povedalo, da njegove postaje ni.
 */
const EMPTY_STATES = {
  start: { title: 'search.start.title', body: 'search.start.body' },
  empty: { title: 'search.empty.title', body: 'search.empty.body' },
  failed: { title: 'search.failed.title', body: 'search.failed.body' },
  offline: { title: 'search.offline.title', body: 'search.offline.body' },
} as const satisfies Record<string, { title: TranslationKey; body: TranslationKey }>;

export interface SearchScreenDeps {
  onSearchQuery(query: string): void;
  onSearchCountry(code: string | null): void;
  onSearchTag(tag: string | null): void;
  onSearchRetry(): void;
  onSearchMore(): void;
  /** Predposluh brez dodajanja (P2 §2.4). */
  onPreview(station: CatalogStation): void;
  onAddFound(station: CatalogStation): void;
}

interface ResultRow {
  station: CatalogStation;
  root: HTMLElement;
  main: HTMLButtonElement;
  action: HTMLSpanElement;
  title: HTMLSpanElement;
  live: HTMLSpanElement;
  liveLabel: HTMLSpanElement;
  sub: HTMLSpanElement;
  subTech: HTMLSpanElement;
  insecure: HTMLSpanElement;
  added: HTMLSpanElement;
  ico: HTMLSpanElement;
  add: HTMLButtonElement;
}

function buildRow(station: CatalogStation, deps: SearchScreenDeps): ResultRow {
  const ico = el('span', { class: 'r-ico' }, [icon('i-radio', 19)]);

  // Dejanje pove bralnik pred imenom; vidna vsebina ostane cela.
  const action = el('span', { class: 'nl-sr' });
  const liveLabel = el('span', { class: 'dot-live__label' });
  const live = el('span', { class: 'dot-live', hidden: '' }, [
    el('span', { class: 'dot-live__dot' }),
    liveLabel,
  ]);
  const title = el('span', { class: 'r-name' });

  const subTech = el('span', { class: 'nw-meta' });
  const sub = el('span', { class: 'r-sub' }, [subTech]);
  // Dve nevtralni znački, dva različna pomena — nobena ni opozorilo.
  const insecure = el('span', { class: 'badge neutral', hidden: '' });
  const added = el('span', { class: 'badge neutral', hidden: '' });

  const main = el('button', { class: 'ao-row__main', type: 'button' }, [
    ico,
    el('span', { class: 'r-main' }, [
      el('span', { class: 'r-title' }, [action, title, live]),
      el('span', { class: 'r-sub-line' }, [sub, insecure, added]),
    ]),
  ]);
  main.addEventListener('click', () => {
    deps.onPreview(station);
  });

  const add = el('button', { class: 'ao-row__act', type: 'button' }, [icon('i-plus', 20)]);
  add.addEventListener('click', () => {
    deps.onAddFound(station);
  });

  const root = el('div', { class: 'ao-row' }, [main, el('span', { class: 'ao-row__acts' }, [add])]);

  return {
    station,
    root,
    main,
    action,
    title,
    live,
    liveLabel,
    sub,
    subTech,
    insecure,
    added,
    ico,
    add,
  };
}

/**
 * Izbira v spustnem seznamu.
 *
 * Ime države izpiše `Intl.DisplayNames` v jeziku vmesnika — vir pošlje samo
 * kodo, in prav je tako: zapisano ime bi bilo v enem od obeh jezikov vedno
 * tuje (isti razlog kot pri seedu, T2 §9). Žanri ostanejo, kot jih pošlje vir;
 * iz kataloga jih pride na stotine in ugibanje bi bilo slabše od surove oznake.
 */
function facetLabel(lang: string, kind: 'countries' | 'tags', facet: CatalogFacet): string {
  if (kind !== 'countries') return facet.value;
  try {
    return new Intl.DisplayNames(lang, { type: 'region' }).of(facet.value) ?? facet.value;
  } catch {
    return facet.value;
  }
}

interface FacetOption {
  value: string;
  label: string;
}

interface Combobox {
  root: HTMLDivElement;
  input: HTMLInputElement;
  clearButton: HTMLButtonElement;
  toggleButton: HTMLButtonElement;
  setOptions(options: readonly FacetOption[]): void;
  /** Uskladi prikazano vrednost s stanjem. Med tipkanjem se ne kliče. */
  setValue(value: string): void;
  isOpen(): boolean;
}

/**
 * Combobox po kanoničnem markupu DS (`ds/js/cmb.js`, glava modula).
 *
 * Vedenje — odpiranje, filtriranje, `aria-activedescendant`, tipkovnica,
 * označevanje ujemanja — nosi modul. Tu je samo drevo in prevod naših izbir
 * v `.cmb-opt`, plus dve stvari, ki ju modul namenoma prepusti klicnemu mestu:
 *
 *   1. **Dostopno ime.** `indexRoot()` postavi `role` in povezave, imena pa ne —
 *      to je vsebina, ne struktura.
 *   2. **Gumb za brisanje ne odda dogodka.** `.cmb-clear` počisti polje in
 *      `data-value`, `cmb:change` pa **ne** sproži. Brez lastnega poslušalca bi
 *      gumb filter vizualno počistil, stanje pa bi ostalo staro — natanko
 *      razkorak, ki je videti kot pokvarjen filter.
 */
function buildCombobox(id: string, onPick: (value: string) => void): Combobox {
  let options: readonly FacetOption[] = [];

  const lead = icon('i-search', 16);
  lead.setAttribute('class', 'icon cmb-lead');

  const input = el('input', { class: 'cmb-input', id, name: id, type: 'text' });

  const clearButton = el('button', { class: 'cmb-clear', type: 'button' }, [icon('i-x', 14)]);
  const toggleButton = el('button', { class: 'cmb-toggle', type: 'button' }, [
    icon('i-chevron-down', 16),
  ]);

  const list = el('div', { class: 'cmb-list' });
  const control = el('div', { class: 'cmb-control' }, [lead, input, clearButton, toggleButton]);
  const root = el('div', { class: 'cmb', 'data-cmb': '' }, [control, list]);

  /**
   * Na dotiku izbiro prevzame bottom sheet z iskanjem.
   *
   * 241 držav v polju, ki je široko 45 % vrstice, je seznam, po katerem se da
   * samo drseti — tudi po tem, ko ga DS v4.23.0 obrne navzgor in zadrži v
   * zaslonu. Sheet da isti nabor čez celo širino, s poljem za iskanje na vrhu,
   * in rezultatov pod seboj ne prekrije.
   *
   * ⛔ **Klik se ne sme razširiti do dokumenta.** `cmb.js` tam prepozna svoj
   * sprožilec (`.cmb-toggle`) in bi odprl **še** vrstični seznam — dva izbirnika
   * hkrati, eden nad drugim. Odkar ima DS `nesylab.sheet.open()` (v4.23.0,
   * [D2 §45](../../../docs/04-delivery/D2-ds-requests.md)), sheet odpremo sami
   * in `stopPropagation()` nas nič ne stane; prej je bil edini izhod odvzem
   * razreda za en klik, ker je sheet odpiral isti dokumentni poslušalec.
   */
  control.addEventListener('click', (event) => {
    if (!isCoarsePointer()) return;

    const target = event.target as Element;
    // Brisanje ostane brisanje; `cmb.js` bi ob njem odprl vrstični seznam.
    event.stopPropagation();
    if (target.closest('.cmb-clear') !== null) return;

    fillPicker({
      title: input.getAttribute('aria-label') ?? '',
      searchLabel: input.getAttribute('aria-label') ?? '',
      searchPlaceholder: input.placeholder,
      emptyText: root.dataset.emptyText ?? '',
      value: root.dataset.value ?? '',
      options,
      onPick,
    });
    // Fokus se ob zaprtju vrne v polje, ne na ovoj: ovoj ga ne sprejme.
    presentSheet(input);
  });

  root.addEventListener('cmb:change', (event) => {
    onPick((event as CustomEvent<{ value: string }>).detail.value);
  });
  clearButton.addEventListener('click', () => {
    onPick('');
  });

  return {
    root,
    input,
    clearButton,
    toggleButton,

    setOptions(next) {
      options = next;
      list.replaceChildren(
        ...next.map((option) => {
          const check = icon('i-check', 15);
          check.setAttribute('class', 'icon check');
          return el(
            'div',
            { class: 'cmb-opt', 'data-value': option.value, 'data-label': option.label },
            [el('span', { class: 'cmb-lbl' }, [option.label]), check],
          );
        }),
      );
      // Nove izbire nimajo ne `id` ne vloge, dokler jih modul ne indeksira.
      rewireCombobox();
    },

    setValue(value) {
      const picked = options.find((option) => option.value === value);
      root.dataset.value = value;
      /**
       * ⚠️ **Brez filtra je polje prazno, ne »katera koli država«.**
       *
       * Prazna vrednost ima svojo izbiro na seznamu in ta ima oznako; če bi jo
       * zapisali v polje, bi bila videti kot izbran filter — z istim besedilom,
       * kot ga sicer nosi ozadnji napis. Razlika bi bila samo v barvi črk.
       */
      input.value = value === '' ? '' : (picked?.label ?? '');
      /**
       * ⚠️ **Na dotiku je polje samo prikaz.** Brez `readonly` bi tap odprl
       * tipkovnico, ta pa bi pojedla pol zaslona pod sheetom, ki je ravno
       * takrat prišel z dna. Tipkanje živi v sheetu; z miško ostane polje
       * polje, ker je vrstični combobox tam pravi vzorec.
       */
      input.readOnly = isCoarsePointer();
      for (const node of list.querySelectorAll('.cmb-opt')) {
        node.setAttribute('aria-selected', String(node.getAttribute('data-value') === value));
      }
    },

    isOpen() {
      return root.classList.contains('open');
    },
  };
}

export function createSearchScreen(
  root: HTMLElement,
  deps: SearchScreenDeps,
): { update(state: AppState): void } {
  const field = createSearchField('catalogSearch', deps.onSearchQuery);

  // Prazna vrednost pomeni »brez filtra« — tako pri izbiri iz seznama kot pri
  // gumbu za brisanje. Store pozna `null`, DS pa `data-value`, zato prevod tu.
  const country = buildCombobox('catalogCountry', (value) => {
    deps.onSearchCountry(value === '' ? null : value);
  });
  const genre = buildCombobox('catalogGenre', (value) => {
    deps.onSearchTag(value === '' ? null : value);
  });

  const toolbar = el('div', { class: 'nw-toolbar' }, [field.root, country.root, genre.root]);

  /**
   * Vrstica nad seznamom: koliko zadetkov in iz katerega vira.
   *
   * Vir je **podatek in ne status** (CLAUDE.md, signal ≠ status): »iz
   * rezervnega imenika« ni opozorilo, je pa pojasnilo, zakaj je zadetkov
   * bistveno manj kot običajno — WebRadioDB jih pozna 1,1 % (T3 §1).
   */
  const summary = el('p', { class: 'nw-screen__body', hidden: '' });
  const list = el('div', { class: 'ao-list nw-stations', role: 'group' });

  const more = el('button', { class: 'btn', type: 'button', hidden: '' });
  more.addEventListener('click', deps.onSearchMore);

  // Prazno stanje ima naslov, opis in dejanje — nikoli le »ni podatkov« (P4 §6).
  const emptyTitle = el('div', { class: 'ftitle' });
  const emptyBody = el('div', { class: 'desc' });
  const emptyAction = el('button', { class: 'btn primary', type: 'button', hidden: '' });
  emptyAction.addEventListener('click', deps.onSearchRetry);
  const empty = el('div', { class: 'empty', hidden: '' }, [emptyTitle, emptyBody, emptyAction]);

  clear(root);
  // Razred korena postavi tovarna zaslonov (`screens/index.ts`) — ta zaslon
  // ne ve, kje v ogrodju stoji, in `hidden` mu prav tako postavlja ona.
  root.append(toolbar, summary, list, more, empty);

  let rows: ResultRow[] = [];
  let renderedIds = '';
  let renderedFacets = '';

  function rebuild(stations: readonly CatalogStation[]): void {
    rows = stations.map((station) => buildRow(station, deps));
    list.replaceChildren(...rows.map((row) => row.root));
    rewireDs();
  }

  /**
   * »Brez filtra« je **prva izbira v seznamu**, ne odsotnost izbire.
   *
   * Gumb za brisanje sicer obstaja, a je 24px širok — pri filtru, ki ga
   * uporabnik pogosto ponastavi, mora obstajati tudi cilj, ki se ga da zadeti
   * s prstom. Vrstica seznama je ~37px in je ta cilj.
   */
  function facetOptions(
    kind: 'countries' | 'tags',
    facets: readonly CatalogFacet[],
    anyLabel: string,
    lang: string,
  ): FacetOption[] {
    return [
      { value: '', label: anyLabel },
      ...facets.map((facet) => ({ value: facet.value, label: facetLabel(lang, kind, facet) })),
    ];
  }

  return {
    update(state) {
      const { lang } = state;
      const search = state.search;

      list.setAttribute('aria-label', translate(lang, 'tab.search'));
      field.update(lang, 'search.field.label', 'search.field.placeholder');
      // Vrednost popravimo le, kadar se razideta — sicer bi vsak izris med
      // tipkanjem premaknil kazalec na konec.
      field.setValue(search.query);

      for (const [box, nameKey] of [
        [country, 'search.filter.country'],
        [genre, 'search.filter.genre'],
      ] as const) {
        // Dostopno ime je vsebina in ga modul namenoma ne postavlja.
        box.input.setAttribute('aria-label', translate(lang, nameKey));
        box.clearButton.setAttribute('aria-label', translate(lang, 'search.filter.clear'));
        box.toggleButton.setAttribute('aria-label', translate(lang, 'search.filter.open'));
        box.root.dataset.emptyText = translate(lang, 'search.filter.noMatch');
      }
      country.input.placeholder = translate(lang, 'search.filter.country.any');
      genre.input.placeholder = translate(lang, 'search.filter.genre.any');

      /**
       * Seznama izbir se prezidata le ob spremembi izbir ali jezika.
       * 241 držav ob vsakem izrisu bi pomenilo 241 novih vozlišč na vsako
       * tipko — in odprt seznam bi se pod prstom zaprl.
       */
      const facetKey = `${lang}|${search.countries.length}|${search.tags.length}`;
      if (facetKey !== renderedFacets) {
        renderedFacets = facetKey;
        country.setOptions(
          facetOptions(
            'countries',
            search.countries,
            translate(lang, 'search.filter.country.any'),
            lang,
          ),
        );
        genre.setOptions(
          facetOptions('tags', search.tags, translate(lang, 'search.filter.genre.any'), lang),
        );
      }

      /**
       * ⛔ **Vrednosti ne pišemo, kadar je seznam odprt.**
       *
       * Takrat uporabnik tipka in `input.value` je njegova poizvedba, ne izbira.
       * Rezultati iskanja medtem prihajajo z omrežja in vsak od njih sproži
       * izris — brez tega pogoja bi mu vsak odgovor pobrisal, kar je natipkal.
       * Isti razlog kot pri iskalnem polju nekaj vrstic višje.
       */
      if (!country.isOpen()) country.setValue(search.countryCode ?? '');
      if (!genre.isOpen()) genre.setValue(search.tag ?? '');

      // Dokler filtrov ni, izbirnika ni: prazen combobox z eno izbiro je polje,
      // ki ne naredi nič.
      country.root.toggleAttribute('hidden', search.countries.length === 0);
      genre.root.toggleAttribute('hidden', search.tags.length === 0);

      const results = search.results;
      const ids = results.map((s) => s.uuid).join(' ');
      if (ids !== renderedIds) {
        renderedIds = ids;
        rebuild(results);
      }

      const hasResults = results.length > 0;
      list.toggleAttribute('hidden', !hasResults);
      more.toggleAttribute('hidden', !hasResults || search.exhausted);
      more.textContent = translate(lang, 'search.more');
      more.disabled = search.status === 'loading';

      /* ── Vrstica povzetka ────────────────────────────────────────── */
      if (search.status === 'loading' && !hasResults) {
        summary.textContent = translate(lang, 'search.loading');
        summary.removeAttribute('hidden');
      } else if (hasResults) {
        // ⚠️ Množina prek `Intl.PluralRules` — slovenščina ima dvojino in vse
        // štiri kategorije (P4 §9). `count === 1 ? a : b` je tu napačno.
        const count = plural(lang, 'search.results.count', results.length);
        summary.textContent =
          search.source === 'webradiodb'
            ? `${count} · ${translate(lang, 'search.source.fallback')}`
            : count;
        summary.removeAttribute('hidden');
      } else {
        summary.setAttribute('hidden', '');
      }

      /* ── Prazna stanja: štiri, ne eno ────────────────────────────── */
      const showEmpty = !hasResults && search.status !== 'loading';
      empty.toggleAttribute('hidden', !showEmpty);

      if (showEmpty) {
        const failed = search.status === 'failed';
        const offline = failed && search.failure === 'offline';
        const which = offline
          ? 'offline'
          : failed
            ? 'failed'
            : search.status === 'idle'
              ? 'start'
              : 'empty';

        // Ključi so našteti in ne sestavljeni: sestavljen ključ bi bil za
        // preverjanje tipov neviden in manjkajoč prevod bi padel šele na
        // zaslonu, in to samo v enem jeziku.
        emptyTitle.textContent = translate(lang, EMPTY_STATES[which].title);
        emptyBody.textContent = translate(lang, EMPTY_STATES[which].body);
        // Poskus znova ima smisel samo ob izpadu vira. Brez povezave bi bil
        // gumb, ki zanesljivo ne naredi ničesar (P4 §5).
        emptyAction.toggleAttribute('hidden', !failed || offline);
        emptyAction.textContent = translate(lang, 'search.failed.action');
      }

      /* ── Vrstice ─────────────────────────────────────────────────── */
      for (const [index, row] of rows.entries()) {
        // Postaja se bere iz svežega seznama, ne iz `row.station`: vrstice se
        // prezidajo ob spremembi `uuid`-jev, vse ostalo pa se posodobi tu.
        const station = results[index] ?? row.station;
        const match = findCatalogMatch(state.stations, station);
        const previewing = state.search.preview?.sourceUuid === station.uuid;
        const playing = previewing && state.playback === 'playing';

        row.action.textContent = translate(lang, 'search.preview', { station: station.name });
        row.title.textContent = station.name;
        row.sub.replaceChildren(formatStationDescription(lang, station), row.subTech);
        row.subTech.textContent = formatStationTech(lang, station);

        row.ico.classList.toggle('mint', previewing);
        row.root.classList.toggle('on', previewing);
        if (previewing) row.main.setAttribute('aria-current', 'true');
        else row.main.removeAttribute('aria-current');

        // V živo nosi piko IN besedo — barvna slepota (CLAUDE.md, pravilo 4).
        row.liveLabel.textContent = translate(lang, 'player.live');
        row.live.toggleAttribute('hidden', !playing);

        /**
         * 🔴 **Samo HTTP se pove VNAPREJ, ne šele ob predvajanju.**
         *
         * 42 % kataloga je samo HTTP (P2 §2) in brskalnik tak tok s HTTPS
         * strani blokira. Značka je nevtralna in ne opozorilo: postaja bo
         * igrala prek našega strežnika, kar je dejstvo o poti, ne o kakovosti.
         */
        row.insecure.textContent = translate(lang, 'station.insecure.badge');
        row.insecure.title = `${translate(lang, 'station.insecure.title')} ${translate(lang, 'station.insecure.hint')}`;
        row.insecure.toggleAttribute('hidden', station.isHttps);

        // Že dodano (P2 §2.6): značka pove stanje, onemogočen gumb pa, da
        // dejanje obstaja in tu ne velja — kar je več, kot pove skrit gumb.
        row.added.textContent = translate(lang, 'search.added');
        row.added.toggleAttribute('hidden', match === null);

        row.add.disabled = match !== null;
        row.add.setAttribute(
          'aria-label',
          translate(lang, match === null ? 'search.add.aria' : 'search.added.aria', {
            station: station.name,
          }),
        );
        row.add.title = translate(lang, match === null ? 'search.add' : 'search.added');
      }
    },
  };
}
